'use client';

import {
  ButtonColour,
  ButtonComponent,
  ButtonSize,
} from '@vaad1st/component.atom.button';
import { IconsSize } from '@vaad1st/component.atom.icons';
import {
  TypographyComponent,
  TypographySize,
  TypographyVariant,
  TypographyWeight,
} from '@vaad1st/component.atom.typography';
import classNames from 'classnames';
import React, { useEffect, useRef, useState } from 'react';
import { HeadingElementType } from 'src/utils/shared-types';
import HTMLRenderer from '../html-renderer/html-renderer.component';
import getIcon from '../icons';
import styles from './table-card.module.less';

type TableItem = {
  icon: string;
  text: string;
  label: string;
};

type TableButton = {
  text: string;
  href: string;
};

export interface TableCardProps {
  id: string;
  title: string;
  titleElement?: HeadingElementType;
  subtitle: string;
  labelPosition: string;
  selectTheme: string;
  showIcons: boolean;
  firstMultiField: Record<string, TableItem>;
  secondMultiField: Record<string, TableItem>;
  button?: TableButton;
  selectColour: string;
  target: string;
}

const TableCard: React.FC<TableCardProps> = ({
  id,
  title,
  titleElement = 'h2',
  subtitle,
  labelPosition,
  selectTheme,
  showIcons,
  firstMultiField = {},
  secondMultiField = {},
  button = { text: '', href: '' },
  selectColour,
  target,
}) => {
  const tableCardClasses = classNames(
    styles['table-card'],
    styles[`table-card__${selectTheme}`],
    styles[`table-card__color-${selectColour}`],
    styles[`table-card__label-position-${labelPosition}`],
  );

  const firstListRefs = useRef<(HTMLLIElement | null)[]>([]);
  const secondListRefs = useRef<(HTMLLIElement | null)[]>([]);
  const [isFirstListOverflow, setIsFirstListOverflow] = useState(false);
  const [isSecondListOverflow, setIsSecondListOverflow] = useState(false);

  const renderIcon = (iconText: string, isFirstField: boolean) => {
    if (!iconText) return null;

    const IconComponent = getIcon(iconText);
    const iconSize = isFirstField ? IconsSize.Sml : IconsSize.Xsm;

    return IconComponent ? <IconComponent size={iconSize} /> : null;
  };

  const renderListItems = (
    items: Record<string, TableItem>,
    isFirstField: boolean,
    refs: React.MutableRefObject<(HTMLLIElement | null)[]>,
    isOverflow: boolean,
  ) => {
    const textSize = isFirstField ? TypographySize.Sml : TypographySize.Xsm;
    const labelSize = isFirstField ? TypographySize.Sml : TypographySize.Xsm;

    return Object.entries(items).map(([key, item], index) => (
      <li
        key={key}
        className={classNames(styles['table-card__list-item'], {
          [styles['table-card__list-item--overflow']]: isOverflow,
        })}
        ref={el => {
          refs.current[index] = el;
        }}>
        {showIcons && renderIcon(item.icon, isFirstField)}

        {item.text && (
          <TypographyComponent
            as="div"
            variant={TypographyVariant.Body}
            size={textSize}
            weight={TypographyWeight.Normal}>
            <HTMLRenderer htmlString={item.text} element="div" />
          </TypographyComponent>
        )}

        {item.label && (
          <TypographyComponent
            as="label"
            variant={TypographyVariant.Body}
            size={labelSize}
            weight={TypographyWeight.Normal}>
            {item.label}
          </TypographyComponent>
        )}
      </li>
    ));
  };

  useEffect(() => {
    const firstListHeights = firstListRefs.current.map(
      item => item?.offsetHeight || 0,
    );
    const secondListHeights = secondListRefs.current.map(
      item => item?.offsetHeight || 0,
    );

    setIsFirstListOverflow(firstListHeights.some(height => height > 22));
    setIsSecondListOverflow(secondListHeights.some(height => height > 22));
  }, [firstMultiField, secondMultiField]);

  const hrefTarget = target === 'new' ? '_blank' : '_self';

  return (
    <div id={id} className={`${tableCardClasses}`} tabIndex={0}>
      <div className={styles['table-card__color-band']}></div>
      <div className={styles['table-card__inner']}>
        <div className={styles['table-card__heading']}>
          {title && (
            <TypographyComponent
              as={titleElement}
              variant={TypographyVariant.Heading}
              size={TypographySize.Mdm}>
              {title}
            </TypographyComponent>
          )}
        </div>
        {firstMultiField && Object.keys(firstMultiField).length > 0 && (
          <ul
            className={`${styles['table-card__list']} ${styles['table-card__list--main']}`}>
            {renderListItems(
              firstMultiField,
              true,
              firstListRefs,
              isFirstListOverflow,
            )}
          </ul>
        )}
        <div className={styles['table-card__subheading']}>
          {subtitle && (
            <TypographyComponent
              as="p"
              variant={TypographyVariant.Heading}
              size={TypographySize.Xsm}>
              {subtitle}
            </TypographyComponent>
          )}
        </div>

        <ul
          className={`${styles['table-card__list']} ${styles['table-card__list--sub']}`}>
          {renderListItems(
            secondMultiField,
            false,
            secondListRefs,
            isSecondListOverflow,
          )}
        </ul>

        {button.text && (
          <ButtonComponent
            as="a"
            size={ButtonSize.Small}
            colour={ButtonColour.Brand}
            href={button.href}
            target={hrefTarget}>
            {button.text}
          </ButtonComponent>
        )}
      </div>
    </div>
  );
};

export default TableCard;
